// Token storage + auth API calls. Pure functions; the React Context
// (AuthContext) consumes these.

import { ApiError, authApi, request } from "./api";
import type { ISODateTime, OperatorRole, UUID } from "./types";

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface AuthenticatedUser {
  id: UUID;
  name: string;
  full_name?: string;
  role: OperatorRole;
  login: string | null;
  nfc_tag: string | null;
  is_active: boolean;
  permissions?: string[];
  /** Ключи модулей, включённых на установке (/auth/me). */
  modules?: string[];
  allowed_stage_ids?: number[];
  allowed_device_ids?: UUID[];
  last_login_at?: ISODateTime | null;
  must_change_password?: boolean;
}

const ACCESS_KEY = "mes:auth:access";
const REFRESH_KEY = "mes:auth:refresh";

export function getAccessToken(): string | null {
  return typeof localStorage !== "undefined" ? localStorage.getItem(ACCESS_KEY) : null;
}

export function getRefreshToken(): string | null {
  return typeof localStorage !== "undefined" ? localStorage.getItem(REFRESH_KEY) : null;
}

export function setTokens(tokens: TokenPair): void {
  localStorage.setItem(ACCESS_KEY, tokens.access_token);
  localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
  window.dispatchEvent(new Event("mes:auth-change"));
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  window.dispatchEvent(new Event("mes:auth-change"));
}

// ---------- helpers ----------

export function hasPermission(user: AuthenticatedUser | null, code: string): boolean {
  if (!user) return false;
  // No permissions array == legacy/old backend response; fail closed for safety.
  return !!user.permissions?.includes(code);
}

export function hasModule(user: AuthenticatedUser | null, key: string): boolean {
  return !!user?.modules?.includes(key);
}

export function hasAnyPermission(user: AuthenticatedUser | null, codes: string[]): boolean {
  if (!user || !user.permissions) return false;
  return codes.some((c) => user.permissions!.includes(c));
}

// ---------- API wrappers ----------

export async function loginByPassword(login: string, password: string): Promise<TokenPair> {
  const tokens = await request<TokenPair>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ login, password }),
  });
  setTokens(tokens);
  return tokens;
}

export async function loginByNfcPin(nfcTag: string, pin: string): Promise<TokenPair> {
  const tokens = await request<TokenPair>("/auth/login-nfc", {
    method: "POST",
    body: JSON.stringify({ nfc_tag: nfcTag, pin }),
  });
  setTokens(tokens);
  return tokens;
}

export async function refreshTokens(): Promise<TokenPair | null> {
  const rt = getRefreshToken();
  if (!rt) return null;
  try {
    const tokens = await request<TokenPair>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: rt }),
    });
    setTokens(tokens);
    return tokens;
  } catch {
    clearTokens();
    return null;
  }
}

export async function fetchMe(): Promise<AuthenticatedUser | null> {
  if (!getAccessToken()) return null;
  try {
    return await request<AuthenticatedUser>("/auth/me");
  } catch (exc) {
    if (exc instanceof ApiError && exc.status === 401) {
      // Try a refresh once; on failure, give up.
      const refreshed = await refreshTokens();
      if (!refreshed) return null;
      try {
        return await request<AuthenticatedUser>("/auth/me");
      } catch {
        return null;
      }
    }
    return null;
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await authApi.changePassword(currentPassword, newPassword);
  // Backend revoked all refresh tokens — our current pair is now invalid.
  // Force logout so the user re-authenticates with the new password.
  clearTokens();
}

export async function logout(): Promise<void> {
  const rt = getRefreshToken();
  if (rt) {
    try {
      await authApi.logout(rt);
    } catch {
      // Best-effort — drop tokens locally regardless of network result.
    }
  }
  clearTokens();
}
